import React, { useState, useMemo, useRef, useCallback } from "react";
import ReactApexChart from "react-apexcharts";
import ChartBurgerMenu from "./ChartBurgerMenu";
import FieldHelp from "../help/FieldHelp";
import HeatmapChart from "./HeatmapChart";
import AIAnalyzerModal from "./AIAnalyzerModal";
import AIAnalyzerButton from "./AIAnalyzerButton";
import { buildChartPayload } from "../utils/aiAnalyzerEngine";

// Robust date parser to handle inconsistent formats (space vs T) and prevent timezone shifts
const parseDateSafe = (dateStr) => {
  if (!dateStr) return NaN;
  if (dateStr instanceof Date) return dateStr.getTime();
  if (typeof dateStr !== "string") return new Date(dateStr).getTime();

  // Parse YYYY-MM-DD HH:mm:ss format as naive local time to avoid timezone shifts
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    const hour = parseInt(match[4] || "0", 10);
    const minute = parseInt(match[5] || "0", 10);
    const second = parseInt(match[6] || "0", 10);
    return new Date(year, month, day, hour, minute, second).getTime();
  }
  return new Date(dateStr).getTime();
};

const GRANULARITY_OPTIONS = [
  { label: "MINUTE", value: "minute" },
  { label: "HOUR", value: "hourly" },
  { label: "DAY", value: "daily" },
];

const SolarGraph = ({
  data = [],
  dateRange,
  granularity = "hourly",
  onGranularityChange,
}) => {
  const chartRef = useRef(null);
  // "chart" = Analysis view, "heatmap" = Heatmap view
  const [viewMode, setViewMode] = useState("chart");
  const [graphType, setGraphType] = useState("area"); // "area" (Line) or "bar"

  // Generate unique IDs for this instance to avoid ApexCharts ID collisions
  const instanceId = useMemo(
    () => `solar-${Math.random().toString(36).substring(2, 9)}`,
    [],
  );
  const mainChartId = `${instanceId}-main`;
  const brushChartId = `${instanceId}-brush`;

  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiModalPayload, setAiModalPayload] = useState(null);

  const handleAnalyzeSolarGraph = () => {
    const labels = (data || []).map((d) => {
      const ts = parseDateSafe(d.timestamp);
      return Number.isNaN(ts) ? String(d.timestamp) : new Date(ts).toLocaleString();
    });
    const datasets = [
      { label: "Feed to Grid (Export)", data: (feedToGridData || []).map((d) => d[1]) },
      { label: "Receive from Grid (Import)", data: (receiveFromGridData || []).map((d) => d[1]) },
      { label: "Solar Generated", data: (data || []).map((d) => d.Solar_Generated_kWh || 0) },
    ];
    const payload = buildChartPayload({
      chartTitle: "Solar Generation & Grid Flow",
      metricName: "Solar & Grid Energy",
      unit: "kWh",
      timestamps: labels,
      datasets: datasets,
      dateRange: dateRange,
    });
    setAiModalPayload(payload);
    setAiModalOpen(true);
  };

  const processSeriesData = useCallback((rawData, key) => {
    if (!rawData || !Array.isArray(rawData)) return [];

    const mapped = rawData
      .map((item) => {
        const ts = parseDateSafe(item.timestamp);
        let val = item[key];
        if (typeof val === "string" && val.trim() === "") val = 0;
        else val = Number(val);

        if (!Number.isFinite(ts) || !Number.isFinite(val)) return null;
        return [ts, val];
      })
      .filter((pt) => pt !== null);

    mapped.sort((a, b) => a[0] - b[0]);

    const deduplicated = [];
    for (const pt of mapped) {
      if (deduplicated.length > 0 && pt[0] === deduplicated[deduplicated.length - 1][0]) {
        deduplicated[deduplicated.length - 1] = pt;
      } else {
        deduplicated.push(pt);
      }
    }

    return deduplicated;
  }, []);

  // Aggregate data by calendar day (sum values per day)
  const aggregateByDay = useCallback((rawData, key) => {
    if (!rawData || !Array.isArray(rawData)) return [];

    const dayMap = new Map();
    rawData.forEach((item) => {
      const ts = parseDateSafe(item.timestamp);
      if (!Number.isFinite(ts)) return;
      let val = item[key];
      if (typeof val === "string" && val.trim() === "") val = 0;
      else val = Number(val);
      if (!Number.isFinite(val)) return;

      // Use midnight of the day as the key
      const d = new Date(ts);
      const dayKey = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      dayMap.set(dayKey, (dayMap.get(dayKey) || 0) + val);
    });

    return Array.from(dayMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([ts, val]) => [ts, Number(val.toFixed(3))]);
  }, []);

  const isDaily = granularity === "daily";
  const isBar = graphType === "bar";

  const feedToGridData = useMemo(
    () => isDaily
      ? aggregateByDay(data, "Grid_Export_kWh")
      : processSeriesData(data, "Grid_Export_kWh"),
    [data, isDaily, processSeriesData, aggregateByDay],
  );

  const receiveFromGridData = useMemo(
    () => isDaily
      ? aggregateByDay(data, "Grid_Import_kWh")
      : processSeriesData(data, "Grid_Import_kWh"),
    [data, isDaily, processSeriesData, aggregateByDay],
  );

  const series = useMemo(() => [
    { name: "Feed to Grid (Export)", data: feedToGridData },
    { name: "Receive from Grid (Import)", data: receiveFromGridData },
  ], [feedToGridData, receiveFromGridData]);

  // Pre-compute x bounds to avoid ApexCharts re-parsing numeric timestamps (parser Error)
  const { xMin, xMax } = useMemo(() => {
    const all = [...feedToGridData, ...receiveFromGridData].map((pt) => pt[0]);
    if (!all.length) return { xMin: undefined, xMax: undefined };
    return { xMin: Math.min(...all), xMax: Math.max(...all) };
  }, [feedToGridData, receiveFromGridData]);

  const chartOptions = useMemo(() => ({
    chart: {
      id: mainChartId,
      type: graphType,
      height: 350,
      zoom: { enabled: !isBar },
      toolbar: { show: false },
      animations: { enabled: false },
    },
    colors: ["#22c55e", "#ef4444"],
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: isBar ? 0 : 2,
    },
    fill: {
      type: isBar ? "solid" : "gradient",
      opacity: isBar ? 0.85 : 1,
      gradient: {
        opacityFrom: 0.6,
        opacityTo: 0.1,
      },
    },
    plotOptions: isBar ? {
      bar: {
        columnWidth: "60%",
        borderRadius: 3,
      },
    } : {},
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      itemMargin: { horizontal: 10, vertical: 5 },
      markers: { radius: 12 },
    },
    xaxis: {
      type: "datetime",
      ...(xMin !== undefined && xMax !== undefined ? { min: xMin, max: xMax } : {}),
      labels: {
        datetimeUTC: false,
        format: isDaily ? "dd MMM" : undefined,
      },
      tooltip: { enabled: !isBar },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      title: {
        text: "Solar Usage (kW)",
        style: { color: "#64748b", fontWeight: 500 },
      },
      labels: {
        formatter: (val) => (typeof val === "number" ? val.toFixed(2) : val),
      },
      min: 0,
    },
    tooltip: {
      x: { format: isDaily ? "dd MMM yyyy" : "dd MMM yyyy HH:mm" },
      y: { formatter: (val) => (typeof val === "number" ? `${val.toFixed(2)} kW` : "–") },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 0,
      yaxis: { lines: { show: true } },
    },
  }), [mainChartId, graphType, isBar, isDaily, xMin, xMax]);

  const brushOptions = useMemo(() => ({
    chart: {
      id: brushChartId,
      height: 100,
      type: "area",
      brush: { target: mainChartId, enabled: true },
      selection: {
        enabled: true,
        ...(xMin !== undefined && xMax !== undefined
          ? { xaxis: { min: xMin, max: xMax } }
          : {}),
      },
      toolbar: { show: false },
    },
    colors: ["#22c55e", "#ef4444"],
    fill: { type: "solid", opacity: 0.4 },
    stroke: { width: 1 },
    xaxis: {
      type: "datetime",
      ...(xMin !== undefined && xMax !== undefined ? { min: xMin, max: xMax } : {}),
      labels: { datetimeUTC: false },
      tooltip: { enabled: false },
    },
    legend: { show: false },
    yaxis: { show: false, tickAmount: 2 },
  }), [brushChartId, mainChartId, xMin, xMax]);

  const hasData = feedToGridData.length > 0 || receiveFromGridData.length > 0;
  const showBrush = !isBar && hasData && (feedToGridData.length + receiveFromGridData.length) > 5
    && xMin !== undefined && xMax !== undefined;

  const granularityLabel = GRANULARITY_OPTIONS.find((o) => o.value === granularity)?.label ?? "HOUR";

  return (
    <div
      style={{
        marginTop: 24,
        padding: "20px",
        background: "#fff",
        borderRadius: "12px",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* ── Header: title + controls ── */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        {/* Chart title */}
        <span style={{ fontSize: 14, fontWeight: 700, color: "#0f172a" }}>
          <FieldHelp id="historical.solarImportExport">Solar Import / Export</FieldHelp>
        </span>

        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>

          {/* ── Analysis / Heatmap toggle ── */}
          <div style={{ display: "flex", background: "#f1f5f9", padding: 2, borderRadius: 8 }}>
            {[
              { label: "Analysis", value: "chart" },
              { label: "Heatmap", value: "heatmap" },
            ].map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setViewMode(opt.value)}
                style={{
                  padding: "4px 10px",
                  borderRadius: 6,
                  border: "none",
                  backgroundColor: viewMode === opt.value ? "#fff" : "transparent",
                  color: viewMode === opt.value ? "#0f172a" : "#64748b",
                  fontSize: "11px",
                  fontWeight: 700,
                  cursor: "pointer",
                  boxShadow: viewMode === opt.value ? "0 1px 3px rgba(0,0,0,0.1)" : "none",
                  transition: "all 0.15s",
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* AI Analyzer & Burger Menu */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <AIAnalyzerButton onClick={handleAnalyzeSolarGraph} />
            <ChartBurgerMenu
              chartRef={chartRef}
              chartTitle={
                granularity === "minute"
                  ? "Solar_Analytics_Minute_Wise"
                  : "Solar_Analytics_Hourly"
              }
              onAIAnalyze={handleAnalyzeSolarGraph}
              exportContainer={true}
              chartData={{
                labels: data.map((d) => {
                  const ts = parseDateSafe(d.timestamp);
                  return Number.isNaN(ts) ? "–" : new Date(ts).toLocaleString();
                }),
                series: [
                  feedToGridData.map((d) => d[1]),
                  receiveFromGridData.map((d) => d[1]),
                ],
                seriesNames: [
                  "Feed to Grid (Export)",
                  "Receive from Grid (Import)",
                ],
              }}
              chartType="solar"
            />
          </div>
        </div>
      </div>

      {/* ── Chart body ── */}
      <div
        id="chart-wrapper"
        ref={chartRef}
        style={{ background: "#fff", padding: "10px", minHeight: hasData ? "auto" : "200px" }}
      >
        {!hasData ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "200px", color: "#64748b" }}>
            No valid data points for this selection
          </div>
        ) : viewMode === "heatmap" ? (
          <SolarHeatmapSection data={data} />
        ) : (
          <>
            <ReactApexChart
              key={`${mainChartId}-${granularity}`}
              options={chartOptions}
              series={series}
              type={graphType}
              height={300}
            />
            {showBrush && (
              <div style={{ marginTop: "-20px" }}>
                <ReactApexChart
                  key={`${brushChartId}-${xMin}-${xMax}`}
                  options={brushOptions}
                  series={series.slice(0, 1)}
                  type="area"
                  height={100}
                />
              </div>
            )}
          </>
        )}
      </div>
      <AIAnalyzerModal
        isOpen={aiModalOpen}
        onClose={() => setAiModalOpen(false)}
        chartPayload={aiModalPayload}
      />
    </div>
  );
};

export default SolarGraph;





/* ==========================================Added
   SOLAR HEATMAP SECTION
   ========================================== */
function SolarHeatmapSection({ data }) {
  // Define structured Heatmap Metadata
  const DAYS_OF_WEEK = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const HOUR_LABELS = useMemo(() => {
    return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);
  }, []);

  // Structural transformation: Maps linear timestamp matrix arrays into a 7x24 weekly matrix
  const processedHeatmap = useMemo(() => {
    if (!data || data.length === 0) {
      return { rows: [], minValue: null, maxValue: null };
    }

    // Accumulators to average out overlapping days of the week (e.g. if looking at a 30-day range)
    const gridSums = Array.from({ length: 7 }, () => Array(24).fill(0));
    const gridCounts = Array.from({ length: 7 }, () => Array(24).fill(0));

    data.forEach((item) => {
      const val = item.Solar_Generated_kWh;
      if (val === null || val === undefined || Number.isNaN(val)) return;

      const timeMs = parseDateSafe(item.timestamp);
      if (Number.isNaN(timeMs)) return;

      const date = new Date(timeMs);

      // Map standard getDay() [0=Sun, 1=Mon...6=Sat] into standard matrix rows [0=Mon...6=Sun]
      const currentDay = date.getDay();
      const dayIdx = currentDay === 0 ? 6 : currentDay - 1;
      const hourIdx = date.getHours();

      gridSums[dayIdx][hourIdx] += val;
      gridCounts[dayIdx][hourIdx] += 1;
    });

    let globalMin = Infinity;
    let globalMax = -Infinity;

    const structuredRows = DAYS_OF_WEEK.map((dayLabel, dIdx) => {
      const values = Array.from({ length: 24 }, (_, hIdx) => {
        const count = gridCounts[dIdx][hIdx];
        if (count === 0) return null; // keeps unrecorded hours empty instead of 0

        const averageValue = gridSums[dIdx][hIdx] / count;

        if (averageValue < globalMin) globalMin = averageValue;
        if (averageValue > globalMax) globalMax = averageValue;

        return Number(averageValue.toFixed(2));
      });

      return {
        label: dayLabel,
        values: values,
      };
    });

    return {
      rows: structuredRows,
      minValue: globalMin === Infinity ? null : globalMin,
      maxValue: globalMax === -Infinity ? null : globalMax,
    };
  }, [data, DAYS_OF_WEEK]);

  if (!processedHeatmap.rows.length) return <div style={{ padding: 20, textAlign: "center" }}>No solar data available for heatmap</div>;

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 16,
        padding: 10,
        boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
      }}
    >
      <HeatmapChart
        rows={processedHeatmap.rows}
        timeLabels={HOUR_LABELS}
        minValue={processedHeatmap.minValue}
        maxValue={processedHeatmap.maxValue}
        loading={false}
        machineColor="#059669"
      />
    </div>
  );
}
